// Branching map generator: 8 layers with widths [2,3,3,3,3,3,2,1].
//
// Map-structure rules:
//  1. Exactly one backport node in the entire map (random slot in BACKPORT_LAYER).
//  2. Layer 0 (width 2) is always one "minion" and one "recruit" in random order.
//  3. Within any layer, no type appears more than (layerWidth - 1) times.
//  4. For each node, if the nearest ancestor in the previous 2 layers share the
//     same type T, T is excluded — no 3 identical in a straight line.
//
// The map seed is deterministic: generateAct(actNumber, runSeed) always produces
// the same structure. Combat rewards use a separate RNG so they vary each run.

import type { NodeType } from "../data/nodes";
import type { EnemyId } from "../data/enemies";
import { CampaignMap, type MapEdge, type MapNode } from "./CampaignMap";
import { LAYER_WIDTHS, BOSS_LAYER, BACKPORT_LAYER, START_LAYER } from "../config/constants";
import { RNG } from "../game/RNG";

// ---- Configurable weights -----------------------------------------------

export interface MapNodeWeights {
  minion:      number;
  jungle:      number;
  recruit:     number;
  randomEvent: number;
  item:        number;
  redBuff:     number;
  blueBuff:    number;
}

export const DEFAULT_NODE_WEIGHTS: MapNodeWeights = {
  minion:      28,
  jungle:      28,
  recruit:     28,
  randomEvent: 5,
  item:        0,
  redBuff:     6,
  blueBuff:    6,
};

// Layer 1 (after the start node) leans toward recruit or jungle on acts 2+.
const LAYER_1_WEIGHTS: readonly [NodeType, number][] = [
  ["recruit", 1],
  ["jungle",  1],
];

// ---- Internal helpers ---------------------------------------------------

function weightsToTuples(w: MapNodeWeights): readonly [NodeType, number][] {
  return [
    ["minion",      w.minion],
    ["jungle",      w.jungle],
    ["recruit",     w.recruit],
    ["randomEvent", w.randomEvent],
    ["item",        w.item],
    ["redBuff",     w.redBuff],
    ["blueBuff",    w.blueBuff],
  ];
}

function weightedPick(rng: RNG, weights: readonly [NodeType, number][]): NodeType {
  const total = weights.reduce((s, [, w]) => s + w, 0);
  let roll = rng.next() * total;
  for (const [type, w] of weights) {
    roll -= w;
    if (roll <= 0) return type;
  }
  return weights[0][0];
}

function layerY(layer: number, totalLayers: number): number {
  return layer / (totalLayers - 1);
}

// Side padding shrinks as layer gets wider so 4-node rows span more than 3-node, etc.
const LAYER_PADDING: Record<number, number> = { 1: 0.5, 2: 0.26, 3: 0.15, 4: 0.07 };

function nodeX(indexInLayer: number, width: number): number {
  if (width === 1) return 0.5;
  const pad = LAYER_PADDING[width] ?? 0.07;
  return pad + (indexInLayer / (width - 1)) * (1 - 2 * pad);
}

function nearestInLayer(
  fromIdx: number,
  fromWidth: number,
  candidates: readonly MapNode[],
): MapNode | undefined {
  if (!candidates.length) return undefined;
  if (fromWidth === 1) return candidates[Math.floor(candidates.length / 2)];
  const toWidth = candidates.length;
  const scaled  = fromIdx * (toWidth - 1) / (fromWidth - 1);
  return candidates.reduce((best, n) =>
    Math.abs(n.indexInLayer - scaled) < Math.abs(best.indexInLayer - scaled) ? n : best,
  );
}

function pickType(
  rng:        RNG,
  idx:        number,
  layerWidth: number,
  builtSoFar: NodeType[],
  prevLayer:  readonly MapNode[],
  prev2Layer: readonly MapNode[],
  pool:       readonly [NodeType, number][],
): NodeType {
  let ancestorBan: NodeType | null = null;
  if (prevLayer.length && prev2Layer.length) {
    const parentType = nearestInLayer(idx, layerWidth, prevLayer)?.type ?? null;
    const gpType     = nearestInLayer(idx, layerWidth, prev2Layer)?.type ?? null;
    if (parentType && gpType && parentType === gpType) ancestorBan = parentType;
  }

  const maxSame = Math.max(1, layerWidth - 1);
  const counts  = new Map<NodeType, number>();
  for (const t of builtSoFar) counts.set(t, (counts.get(t) ?? 0) + 1);

  const filtered = pool.filter(([t]) =>
    t !== ancestorBan && (counts.get(t) ?? 0) < maxSame,
  );

  return weightedPick(rng, filtered.length ? filtered : pool);
}

// ---- Public API ---------------------------------------------------------

export function generateAct(
  actNumber: number,
  runSeed:   number,
  weights:   MapNodeWeights = DEFAULT_NODE_WEIGHTS,
): CampaignMap {
  const seed = ((runSeed ^ (actNumber * 0x9E3779B9)) >>> 0) || 1;
  const rng  = new RNG(seed);

  const normalPool = weightsToTuples(weights);
  const nodes:      MapNode[]   = [];
  const layerNodes: MapNode[][] = [];

  const isAct1 = actNumber === 1;
  // Act 1 only: layer 1 is a forced 2-node row (recruit + non-gromp jungle) in random order.
  const layer1Roles: NodeType[] = rng.next() < 0.5 ? ["recruit", "jungle"] : ["jungle", "recruit"];

  for (let layer = 0; layer < LAYER_WIDTHS.length; layer++) {
    const width      = LAYER_WIDTHS[layer];
    const prevLayer  = layerNodes[layer - 1] ?? [];
    const prev2Layer = layerNodes[layer - 2] ?? [];
    const layerList: MapNode[] = [];
    let healPlacedThisLayer = false;

    for (let idx = 0; idx < width; idx++) {
      let type: NodeType;
      let campType: string | undefined;

      if (layer === BOSS_LAYER) {
        type = "boss";
      } else if (layer === BACKPORT_LAYER) {
        if (!healPlacedThisLayer) {
          type = "heal";
          healPlacedThisLayer = true;
        } else {
          type = pickType(rng, idx, width, layerList.map(n => n.type), prevLayer, prev2Layer, normalPool);
          if (type === "jungle") campType = pickJungleCamp(rng, layer);
        }
      } else if (layer === START_LAYER) {
        type = "start";
      } else if (layer === START_LAYER + 1) {
        if (isAct1) {
          type = layer1Roles[idx];
          if (type === "jungle") campType = pickNonGrompCamp(rng);
        } else {
          type = pickType(rng, idx, width, layerList.map(n => n.type), prevLayer, prev2Layer, LAYER_1_WEIGHTS);
          if (type === "jungle") campType = pickJungleCamp(rng, layer);
        }
      } else {
        type = pickType(rng, idx, width, layerList.map(n => n.type), prevLayer, prev2Layer, normalPool);
        if (type === "jungle") campType = pickJungleCamp(rng, layer);
      }

      const node: MapNode = {
        id: `a${actNumber}_l${layer}_${idx}`,
        type,
        layer,
        indexInLayer: idx,
        x: nodeX(idx, width),
        y: layerY(layer, LAYER_WIDTHS.length),
        campType,
      };
      layerList.push(node);
      nodes.push(node);
    }
    layerNodes[layer] = layerList;
  }

  const edges = generateEdges(nodes);
  const startNode = nodes.find(n => n.type === "start")!;
  return new CampaignMap(nodes, edges, startNode.id, new Set([startNode.id]));
}

const JUNGLE_CAMP_TYPES = ["gromp", "wolf", "raptor", "krug"] as const;
const NON_GROMP_CAMPS   = ["wolf", "raptor", "krug"] as const;

function pickJungleCamp(rng: RNG, _layer: number): string {
  return JUNGLE_CAMP_TYPES[Math.floor(rng.next() * JUNGLE_CAMP_TYPES.length)];
}

function pickNonGrompCamp(rng: RNG): string {
  return NON_GROMP_CAMPS[Math.floor(rng.next() * NON_GROMP_CAMPS.length)];
}

// ---- Edge generation ----------------------------------------------------

function getReachableIndices(fromIdx: number, fromWidth: number, toWidth: number): number[] {
  if (fromWidth === 1 || toWidth === 1) {
    return Array.from({ length: toWidth }, (_, i) => i);
  }
  // When narrowing, outer nodes only connect straight down (not inward).
  if (fromWidth > toWidth) {
    if (fromIdx === 0) return [0];
    if (fromIdx === fromWidth - 1) return [toWidth - 1];
  }
  const scaled = fromIdx * (toWidth - 1) / (fromWidth - 1);
  const result: number[] = [];
  for (let j = 0; j < toWidth; j++) {
    if (Math.abs(j - scaled) <= 1 + 1e-9) result.push(j);
  }
  return result;
}

function generateEdges(nodes: readonly MapNode[]): MapEdge[] {
  const edges:    MapEdge[] = [];
  const edgeKeys = new Set<string>();
  const addEdge  = (from: MapNode, to: MapNode): void => {
    const key = `${from.id}->${to.id}`;
    if (!edgeKeys.has(key)) { edgeKeys.add(key); edges.push({ from: from.id, to: to.id }); }
  };

  for (let layer = 0; layer < LAYER_WIDTHS.length - 1; layer++) {
    const current = nodes.filter(n => n.layer === layer).sort((a, b) => a.indexInLayer - b.indexInLayer);
    const next    = nodes.filter(n => n.layer === layer + 1).sort((a, b) => a.indexInLayer - b.indexInLayer);
    for (const from of current) {
      for (const j of getReachableIndices(from.indexInLayer, current.length, next.length)) {
        addEdge(from, next[j]);
      }
    }
  }
  return edges;
}

// ---- Renderer helpers ---------------------------------------------------

function campShortName(campType: string | undefined): string {
  switch (campType) {
    case "raptor":  return "Raptors";
    case "wolf":    return "Wolves";
    case "krug":    return "Krugs";
    case "scuttle": return "Scuttle";
    default:        return "Gromp";
  }
}

export function nodeShortLabel(node: MapNode): string {
  switch (node.type) {
    case "start":       return "START";
    case "minion":      return "Minions";
    case "jungle":      return campShortName(node.campType);
    case "recruit":     return "+1 Champion";
    case "randomEvent": return "Random";
    case "boss":        return "BOSS";
    case "item":        return "Item";
    case "heal":        return "Rest";
    case "redBuff":     return "Red Buff";
    case "blueBuff":    return "Blue Buff";
  }
}

const BOSS_IMAGE: Partial<Record<string, string>> = {
  demacia:    "demacia.png",
  freljord:   "freljord.png",
  noxus:      "noxus.png",
  piltover:   "piltoverZaun.png",
  ixtal:      "ixtal.png",
  shurima:    "shurima.png",
  targon:     "targon.png",
};

export function nodeImagePath(node: MapNode, region?: string): string | null {
  const base = `${import.meta.env.BASE_URL}images/map/`;
  switch (node.type) {
    case "minion":      return `${base}minion.png`;
    case "jungle": {
      const campImg: Record<string, string> = { gromp: "gromp.png", wolf: "wolf.png", raptor: "raptor.png", krug: "krug.png", scuttle: "scuttle.png" };
      return `${base}${campImg[node.campType ?? "gromp"] ?? "gromp.png"}`;
    }
    case "recruit":     return `${base}recruit.png`;
    case "boss": {
      const file = region ? BOSS_IMAGE[region] : null;
      return file ? `${base}boss/${file}` : `${base}boss.png`;
    }
    case "randomEvent": return `${base}random.png`;
    case "item":        return `${base}tournament.png`;
    case "heal":        return `${base}healing.png`;
    case "redBuff":     return `${base}redBuff.png`;
    case "blueBuff":    return `${base}blueBuff.png`;
    default:            return null;
  }
}

export function nodeGlyph(type: NodeType): string {
  switch (type) {
    case "start":       return "▶";
    case "minion":      return "M";
    case "jungle":      return "J";
    case "recruit":     return "R";
    case "randomEvent": return "?";
    case "boss":        return "BOSS";
    case "item":        return "I";
    case "heal":        return "✚";
    case "redBuff":     return "🔴";
    case "blueBuff":    return "🔵";
  }
}

export { generateAct as generateVerticalSlice };
